import React, { useState, useEffect } from 'react';
import {
  Drawer,
  Form,
  Input,
  Select,
  Button,
  InputNumber,
  message,
  Row,
  Col,
  Card,
  Divider,
  Tag,
} from 'antd';
import { PlusOutlined, WalletOutlined, DollarOutlined, DeleteOutlined, CalendarOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import {
  IncomeSource,
  IncomeTransaction,
  SourceWallet,
  incomeService,
  formatCurrency,
} from '../../services/incomeService';
import { DatePickerModal } from '../common/DatePickerModal';
import { SellectWalletModalMobile } from './SellectWalletModalMobile';
import styles from '../common/DrawerModalMobile.module.css';

interface IncomeTransactionModalProps {
  isOpen: boolean;
  onClose: () => void;
  transaction?: IncomeTransaction;
  onSuccess?: () => void;
}

const SOURCE_ICONS = [
  '💰', '💼', '🏢', '🏪', '🏭', '🏦', '📈', '💻', '🎓', '🏥', '🎨', '✈️', '🚗', '🏠', '📱', '🎮'
];

const SOURCE_COLORS = [
  '#10B981', '#3B82F6', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4', '#6B7280',
  '#84CC16', '#F97316', '#6366F1', '#14B8A6', '#F43F5E', '#A855F7', '#EAB308', '#22C55E'
];

export const IncomeTransactionModalMobile: React.FC<IncomeTransactionModalProps> = ({
  isOpen,
  onClose,
  transaction,
  onSuccess,
}) => {
  const [sources, setSources] = useState<IncomeSource[]>([]);
  const [wallets, setWallets] = useState<SourceWallet[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingWallets, setLoadingWallets] = useState(false);
  const [form] = Form.useForm();
  const [showSourceModal, setShowSourceModal] = useState(false);
  const [sourceForm] = Form.useForm();
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Dayjs>(dayjs());
  const [showWalletModal, setShowWalletModal] = useState(false);
  const [selectedWallet, setSelectedWallet] = useState<SourceWallet | null>(null);

  const isEdit = !!transaction;

  useEffect(() => {
    if (isOpen) {
      loadSources();
      loadWallets();
      if (transaction) {
        const transactionDate = dayjs(transaction.income_date);
        setSelectedDate(transactionDate);
        form.setFieldsValue({
          source_id: transaction.source_id,
          wallet_id: transaction.wallet_id,
          amount: transaction.amount,
          income_date: transactionDate,
          notes: transaction.notes || '',
        });
      } else {
        const today = dayjs();
        setSelectedDate(today);
        form.resetFields();
        form.setFieldsValue({
          income_date: today,
        });
        setSelectedWallet(null);
      }
    } else {
      // Reset khi modal đóng
      setSelectedWallet(null);
      setShowSourceModal(false);
      setShowDatePicker(false);
      setShowWalletModal(false);
    }
  }, [isOpen, transaction, form]);

  // Separate useEffect để set selectedWallet sau khi wallets đã load
  useEffect(() => {
    if (transaction && wallets.length > 0) {
      const selectedWalletData = wallets.find(w => w.id === transaction.wallet_id);
      if (selectedWalletData) {
        setSelectedWallet(selectedWalletData);
      } else {
        console.log('Không tìm thấy ví với ID:', transaction.wallet_id, 'trong danh sách:', wallets.map(w => ({ id: w.id, name: w.name })));
      }
    }
  }, [transaction, wallets]);

  const loadSources = async () => {
    try {
      const sourcesData = await incomeService.getSources();
      setSources(sourcesData);
    } catch (error) {
      message.error('Không thể tải nguồn thu nhập');
    }
  };

  const loadWallets = async () => {
    try {
      setLoadingWallets(true);
      const walletsData = await incomeService.getWallets();
      setWallets(walletsData.filter(wallet => wallet.is_active));
    } catch (error) {
      message.error('Không thể tải danh sách ví');
    } finally {
      setLoadingWallets(false);
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const submitData = {
        source_id: values.source_id,
        wallet_id: values.wallet_id,
        amount: values.amount,
        transaction_type: 'income' as const,
        income_date: values.income_date.format('YYYY-MM-DD'),
        notes: values.notes || '',
      };

      if (isEdit && transaction) {
        await incomeService.updateTransaction(transaction.id, submitData);
        message.success('Cập nhật giao dịch thành công');
      } else {
        await incomeService.createTransaction(submitData);
        message.success('Thêm giao dịch thành công');
      }
      
      // Reload dữ liệu ví để cập nhật số dư
      await loadWallets();
      
      // Gọi callback onSuccess để refresh màn hình gốc
      onSuccess?.();
      onClose();
    } catch (error: any) {
      if (error.errorFields) {
        message.error('Vui lòng nhập đầy đủ thông tin');
      } else {
        // Hiển thị thông báo chi tiết từ API
        const errorMessage = error.response?.data?.error || error.message || 'Có lỗi xảy ra khi lưu giao dịch';
        message.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAddSource = async () => {
    try {
      const values = await sourceForm.validateFields();
      setLoading(true);

      await incomeService.createSource({
        name: values.name,
        icon: values.icon,
        color: values.color,
        description: values.description,
      });

      setShowSourceModal(false);
      sourceForm.resetFields();
      await loadSources();
      message.success('Thêm nguồn thu nhập thành công');
    } catch (error: any) {
      if (error.errorFields) {
        message.error('Vui lòng nhập tên nguồn thu nhập');
      } else {
        message.error('Có lỗi xảy ra khi thêm nguồn thu nhập');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDateSelect = (date: Dayjs) => {
    setSelectedDate(date);
    form.setFieldsValue({ income_date: date });
    setShowDatePicker(false);
  };

  const handleWalletSelect = (wallet: SourceWallet) => {
    setSelectedWallet(wallet);
    form.setFieldsValue({ wallet_id: wallet.id });
  };

  const getSourceDisplayName = (source: IncomeSource) => {
    return `${source.icon} ${source.name}`;
  };

  const getWalletDisplayName = (wallet: SourceWallet) => {
    return `${wallet.name} (${wallet.source_name})`;
  };

  const getWalletBalanceDisplay = (wallet: SourceWallet) => {
    return `${formatCurrency(wallet.current_balance)}`;
  };

  return (
    <>
      <Drawer
        title={isEdit ? 'Chỉnh sửa giao dịch' : 'Thêm giao dịch mới'}
        open={isOpen}
        onClose={onClose}
        placement="bottom"
        height="85vh"
        className={styles.drawer}
        bodyStyle={{ 
          padding: 0,
          userSelect: 'none',
          WebkitUserSelect: 'none',
          MozUserSelect: 'none',
          msUserSelect: 'none',
          WebkitTouchCallout: 'none',
          WebkitTapHighlightColor: 'transparent'
        }}
      >
        <div className={styles.drawerContent}>
          <div className={styles.drawerBody}>
            <Form
              form={form}
              layout="vertical"
              initialValues={{
                income_date: dayjs(),
              }}
            >
              <Form.Item
                name="amount"
                label="Số tiền"
                rules={[{ required: true, message: 'Vui lòng nhập số tiền' }]}
                className={styles.formItem}
              >
                <InputNumber
                  className={styles.inputNumber}
                  placeholder="Nhập số tiền"
                  min={0}
                  formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  inputMode="numeric"
                  pattern="[0-9]*"
                />
              </Form.Item>



              <Form.Item
                name="source_id"
                label="Nguồn thu nhập"
                rules={[{ required: true, message: 'Vui lòng chọn nguồn thu nhập' }]}
                className={styles.formItem}
              >
                <Select
                  className={styles.select}
                  placeholder="Chọn nguồn thu nhập"
                  dropdownRender={(menu) => (
                    <>
                      {menu}
                      <Divider style={{ margin: '8px 0' }} />
                      <Button
                        type="text"
                        icon={<PlusOutlined />}
                        onClick={() => setShowSourceModal(true)}
                        className={styles.dropdownButton}
                      >
                        Thêm nguồn mới
                      </Button>
                    </>
                  )}
                >
                  {sources.map((source) => (
                    <Select.Option key={source.id} value={source.id}>
                      <div className={styles.categoryOption}>
                        <span>{source.icon}</span>
                        <span>{source.name}</span>
                      </div>
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                name="wallet_id"
                label="Ví"
                rules={[{ required: true, message: 'Vui lòng chọn ví' }]}
                className={styles.formItem}
              >
                <div className={styles.dateInputWrapper}>
                  <Input
                    className={styles.dateInput}
                    placeholder="Chọn ví"
                    value={selectedWallet ? getWalletDisplayName(selectedWallet) : ''}
                    readOnly
                    onClick={() => setShowWalletModal(true)}
                    suffix={<WalletOutlined style={{ color: '#666' }} />}
                  />
                </div>
              </Form.Item>

              <Form.Item
                name="income_date"
                label="Ngày giao dịch"
                rules={[{ required: true, message: 'Vui lòng chọn ngày' }]}
                className={styles.formItem}
              >
                <div className={styles.dateInputWrapper}>
                  <Input
                    className={styles.dateInput}
                    placeholder="Chọn ngày"
                    value={selectedDate.format('DD/MM/YYYY')}
                    readOnly
                    onClick={() => setShowDatePicker(true)}
                    suffix={<CalendarOutlined style={{ color: '#666' }} />}
                  />
                </div>
              </Form.Item>

              <Form.Item
                name="notes"
                label="Ghi chú"
                className={styles.formItem}
              >
                <Input.TextArea
                  className={styles.textArea}
                  placeholder="Ghi chú (tùy chọn)"
                  rows={3}
                />
              </Form.Item>
            </Form>
          </div>

          <div className={styles.drawerFooter}>
            <div className={styles.drawerFooterRow}>
              <Button onClick={onClose} className={`${styles.drawerFooterButton} ${styles.default}`}>
                Hủy
              </Button>
              <Button
                type="primary"
                loading={loading}
                onClick={handleSubmit}
                className={`${styles.drawerFooterButton} ${styles.primary}`}
              >
                {isEdit ? 'Cập nhật' : 'Thêm'}
              </Button>
            </div>
          </div>
        </div>
      </Drawer>

      {/* Modal thêm nguồn thu nhập mới */}
      <Drawer
        title="Thêm nguồn thu nhập mới"
        open={showSourceModal}
        onClose={() => setShowSourceModal(false)}
        placement="bottom"
        height="70vh"
        className={styles.categoryDrawer}
        bodyStyle={{ 
          padding: 0,
          userSelect: 'none',
          WebkitUserSelect: 'none',
          MozUserSelect: 'none',
          msUserSelect: 'none',
          WebkitTouchCallout: 'none',
          WebkitTapHighlightColor: 'transparent'
        }}
      >
        <div className={styles.drawerContent}>
          <div className={styles.categoryDrawerBody}>
            <Form
              form={sourceForm}
              layout="vertical"
              initialValues={{
                icon: '💰',
                color: '#10B981',
              }}
            >
              <Form.Item
                name="name"
                label="Tên nguồn thu nhập"
                rules={[{ required: true, message: 'Vui lòng nhập tên nguồn thu nhập' }]}
                className={styles.formItem}
              >
                <Input placeholder="Nhập tên nguồn thu nhập" />
              </Form.Item>

              <Form.Item
                name="icon"
                label="Icon"
                className={styles.formItem}
              >
                <Select className={styles.select} placeholder="Chọn icon">
                  {SOURCE_ICONS.map((icon) => (
                    <Select.Option key={icon} value={icon}>
                      <div className={styles.iconOption}>
                        <span>{icon}</span>
                      </div>
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                name="color"
                label="Màu sắc"
                className={styles.formItem}
              >
                <Select className={styles.select} placeholder="Chọn màu sắc">
                  {SOURCE_COLORS.map((color) => (
                    <Select.Option key={color} value={color}>
                      <div className={styles.colorOption}>
                        <div
                          className={styles.colorCircle}
                          style={{ backgroundColor: color }}
                        />
                        <span>{color}</span>
                      </div>
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                name="description"
                label="Mô tả"
                className={styles.formItem}
              >
                <Input.TextArea
                  placeholder="Mô tả nguồn thu nhập (tùy chọn)"
                  rows={2}
                />
              </Form.Item>
            </Form>
          </div>

          <div className={styles.categoryDrawerFooter}>
            <div className={styles.categoryDrawerFooterRow}>
              <Button onClick={() => setShowSourceModal(false)} className={`${styles.categoryDrawerFooterButton} ${styles.default}`}>
                Hủy
              </Button>
              <Button
                type="primary"
                loading={loading}
                onClick={handleAddSource}
                className={`${styles.categoryDrawerFooterButton} ${styles.primary}`}
              >
                Thêm
              </Button>
            </div>
          </div>
        </div>
      </Drawer>

      {/* Custom Date Picker Modal */}
      <DatePickerModal
        isOpen={showDatePicker}
        onClose={() => setShowDatePicker(false)}
        onSelect={handleDateSelect}
        selectedDate={selectedDate}
        title="Chọn ngày giao dịch"
      />

      {/* Wallet Selection Modal */}
      <SellectWalletModalMobile
        isOpen={showWalletModal}
        onClose={() => setShowWalletModal(false)}
        onSelect={handleWalletSelect}
        selectedWalletId={selectedWallet?.id}
      />
    </>
  );
};
